import React, { useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { theme } from "../../../index";
import Editor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import CKEditorImageUpload from "../../../Utils/CKEditorImageUpload";
import { updateReportedQuestionCorrectPrompt, updateReportedQuestionSol, updateReportedQuestionVal, updateReportedQuestionStatus } from "../../../api/testSeries/QuestionReportSystem";
import { CK_EDITOR_SCRIPT } from "../TestSeries/QuestionLanguageVarient";


// Format date to a readable string
const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
        const date = new Date(dateString);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
        console.error("Error formatting date:", error);
        return "Invalid Date";
    }
};

function QuestionReportSystemRow(props) {
    const { row, index, onStatusUpdate, currentPage, itemCount } = props;

    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [solution, setSolution] = useState(row?.questionLanguageVarient?.solution);
    const [correctOption, setCorrectOption] = useState({ value: undefined, prompt: row.question.correctPrompt });
    const [questionVal, setQuestionVal] = useState(row?.questionLanguageVarient?.value);
    const [reportStatus, setReportStatus] = useState(row.reportStatus);
    const [loadingStates, setLoadingStates] = useState({});

    // Calculate the correct serial number based on current page and item count
    const calculateSerialNumber = () => {
        return (currentPage * itemCount) + index + 1;
    };

    function getDistinctTextValues(array) {
        const textValues = array.map(item => item.text);
        const uniqueTextValues = [...new Set(textValues)];
        return uniqueTextValues;
    }

    const toggleLoading = (key, isLoading) => {
        setLoadingStates(prev => ({ ...prev, [key]: isLoading }));
    };

    const handleUpdateReportStatus = async () => {
        toggleLoading("updateReportStatus", true);
        const newStatus = reportStatus.toLowerCase() === "unresolved" ? "RESOLVED" : "UNRESOLVED";

        try {
            const res = await updateReportedQuestionStatus(row.id, newStatus);
            if (res.status) {
                setReportStatus(newStatus);
                if (typeof onStatusUpdate === 'function') {
                    onStatusUpdate(row.id, newStatus);
                }
            }
        } catch (error) {
            console.error("Error updating report status:", error);
        } finally {
            toggleLoading("updateReportStatus", false);
        }
    };

    const handleUpdate = async (updateFn, args, key) => {
        toggleLoading(key, true);
        try {
            const result = await updateFn(...args);
            if (result && result.status && typeof onStatusUpdate === 'function') {
                onStatusUpdate();
            }
        } catch (error) {
            console.error(`Error in ${key}:`, error);
        } finally {
            toggleLoading(key, false);
        }
    };

    return (
        <>
            <tr>
                <td align="">{calculateSerialNumber()}</td>
                <td align="">{formatDate(row.createdAt)}</td>
                <td align="">{row.question.questionNumber}</td>
                <td align="">{row.testSeries.testSeriesExamTestTypeSubCategory.testSeriesExamTestType.title}</td>
                <td align="">{row.testSeries.testSeriesExamTestTypeSubCategory.title}</td>
                <td align="">{row.testSeries.title}</td>
                <td align="">{reportStatus}</td>
                <td align="">
                    {getDistinctTextValues(row.reportUsers).map((val, idx) => (
                        <li key={idx}>{val}</li>
                    ))}
                </td>

                <td align="">
                    <Button
                        onClick={() => {
                            setIsAddModalVisible(true);
                            setTimeout(() => {
                                CK_EDITOR_SCRIPT()
                            }, 500);
                        }}
                        aria-label="view"
                        className="btn btn-primary mr-1"
                        disabled={loadingStates["view"]}
                    >
                        View
                    </Button>
                    <Button
                        onClick={handleUpdateReportStatus}
                        aria-label="update-status"
                        className="btn btn-primary mr-1"
                        disabled={loadingStates["updateReportStatus"]}
                    >
                        {loadingStates["updateReportStatus"] ? "Processing..." : `Mark as ${reportStatus === "UNRESOLVED" ? "resolved" : "unresolved"}`}
                    </Button>
                </td>
            </tr>

            <Modal
                size="lg"
                show={isAddModalVisible}
                onHide={() => setIsAddModalVisible(false)}
                aria-labelledby="example-modal-sizes-title-lg"
                style={{ zIndex: 10000 }}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="example-modal-sizes-title-lg">Question Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        {/* Question Update Section */}
                        <div className="form-row">
                            <div className="col-12">
                                <label>Question</label>
                                <CKEditor
                                    editor={Editor}
                                    data={questionVal}
                                    onChange={(event, editor) => {
                                        setQuestionVal(editor.getData());

                                    }}
                                    config={{ extraPlugins: [CKEditorImageUpload] }}

                                />

                            </div>
                            <Button
                                onClick={() => handleUpdate(updateReportedQuestionVal, [row?.questionLanguageVarient?.id, questionVal], "updateQuestionVal")}
                                disabled={loadingStates["updateQuestionVal"]}
                            >
                                {loadingStates["updateQuestionVal"] ? "Updating..." : "Update Question"}
                            </Button>
                        </div>

                        {/* Options Update Section */}
                        <div>
                            <label>Options</label>
                            {row?.questionLanguageVarient?.options?.map((option, idx) => (
                                <div key={idx} className="form-row mt-3" style={{ alignItems: "center" }}>
                                    <div className="col-12">
                                        <div className="mr-2" style={{ display: "flex", gap: "5px" }}>
                                            <input
                                                type="radio"
                                                name="dynamic-radio"
                                                value={option.value}
                                                onChange={() => setCorrectOption({ value: option.value, prompt: option.prompt })}
                                                defaultChecked={row.question.correctPrompt === option.prompt}
                                            />
                                            <label>{option.value}</label>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <Button
                                onClick={() => handleUpdate(updateReportedQuestionCorrectPrompt, [row?.question?.id, correctOption?.prompt], "updateCorrectOption")}
                                disabled={loadingStates["updateCorrectOption"]}
                            >
                                {loadingStates["updateCorrectOption"] ? "Updating..." : "Update Correct Option"}
                            </Button>
                        </div>

                        {/* Solution Update Section */}
                        <div className="form-row">
                            <div className="col-12">
                                <label>Solution</label>
                                <CKEditor
                                    editor={Editor}
                                    data={solution}
                                    onChange={(event, editor) => setSolution(editor.getData())}
                                    config={{ extraPlugins: [CKEditorImageUpload] }}
                                />
                            </div>
                            <Button
                                onClick={() => handleUpdate(updateReportedQuestionSol, [row?.questionLanguageVarient?.id, solution], "updateSolution")}
                                disabled={loadingStates["updateSolution"]}
                            >
                                {loadingStates["updateSolution"] ? "Updating..." : "Update Solution"}
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setIsAddModalVisible(false)}
                        style={{ backgroundColor: theme.greyColor, color: theme.primaryColor }}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default QuestionReportSystemRow;
